import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { PrimeNgModule } from '../../../core/shared/primeng.module';
import { SharedComponents } from '../../shared.module';

@Component({
  selector: 'app-stacked-chart',
  standalone: true,
  imports: [PrimeNgModule, CommonModule, ...SharedComponents],
  templateUrl: './stacked-chart.component.html',
  styleUrl: './stacked-chart.component.scss'
})
export class StackedChartComponent {
  @Input() chartType: any = "bar";
  @Input() data: any = {};
  @Input() isHorizontal?: boolean = false;

  rootStyles = getComputedStyle(document.documentElement);

  chartData: any;
  chartOptions: any;
  labels: string[] = [];
  values: number[] = [];
  dataset: any;
  categories: any;

  // Pagination state
  currentPage = 0;
  pageSize = 6; // Show 10 records at a time
  pageNumber = 1;
  paginatedLabels: string[] = [];
  paginatedDataset: any = {};
  isStacked:boolean = true;

  ngOnInit() {
    this.dataset = this.data?.data ? this.data?.data : {};
    this.getPaginatedData();
    this.getChartOptions();
  }

   /**
   * get chart options for the chart
   */
  getChartOptions(){
    this.chartOptions = {
      indexAxis: this.isStacked ? "y" : "x", // orientation horizontal if it's a stacked bar chart
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          title: {
            display: true,
            text: this.data?.meta[0]?.x,
          },
          stacked: this.isStacked,
          beginAtZero: true,
          ticks: {
            color: this.rootStyles.getPropertyValue("--charcoal").trim(),
            font: {
              size: 12,
            },
          },
          grid: {
            color: this.rootStyles
              .getPropertyValue("--grey3")
              .trim(),
          },
        },
        y: {
          title: {
            display: true,
            text: this.data?.meta[0]?.y,
          },
          stacked: this.isStacked,
          ticks: {
            autoSkip: false,
            color: this.rootStyles.getPropertyValue("--charcoal").trim(),
            font: {
              size: 12,
            },
            align: 'left',
            callback: (value:any, index:any, values:any) => {
              const label = this.labels[index]; // Get the actual label
              return typeof label === "string" ? this.ellipsizeLabel(label, 15) : label;
            },
          },
          grid: {
            color: this.rootStyles
              .getPropertyValue("--grey3")
              .trim(),
          },
        },
      },
      plugins: {
        legend: {
          position: "right",
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.dataset.label || "";
              const value = context.raw || 0;
              return `${label}: ${value}`;
            },
          },
        },
        datalabels: {
          display: false,
        },
      },
    };
  }

  /**
   * Extracts paginated data and updates the chart.
   */
  getPaginatedData() {
    this.labels = Object.keys(this.dataset);
    this.categories = Array.from(
      new Set(this.labels.flatMap((key) => Object.keys(this.dataset[key])))
    );

    this.isStacked = this.categories.length > 1;
    this.categories = this.setPriorityForCategories();

    // Slice data based on pagination
    const start = this.currentPage * this.pageSize;
    const end = start + this.pageSize;
    this.paginatedLabels = this.labels.slice(start, end);
    this.paginatedDataset = Object.fromEntries(
      this.paginatedLabels.map((label) => [label, this.dataset[label]])
    );

    this.updateChart();
  }

  /**
   * Updates the chart dataset based on paginated data.
   */
  updateChart() {
    if (this.isStacked) {
      this.chartData = {
        labels: this.paginatedLabels,
        datasets: this.categories.map((category: any, index: any) => ({
          label: this.capitalizeFirstLetter(category),
          data: this.paginatedLabels.map(
            (key) => this.paginatedDataset[key][category] || 0
          ),
          backgroundColor: this.getDynamicColor(index),
        })),
      };
    } 
  }

  capitalizeFirstLetter(word: string): string {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }

    /**
   * Generates a dynamic color based on the index.
   * @param {number} index - The index for which to generate the color.
   * @returns {string} - The dynamic color.
   */
    getDynamicColor(index: number): string {
      const colors = [
        this.rootStyles.getPropertyValue("--red").trim(),
        this.rootStyles.getPropertyValue("--magenta").trim(),
        this.rootStyles.getPropertyValue("--light-blue").trim(),
        this.rootStyles.getPropertyValue("--green").trim(),
        this.rootStyles.getPropertyValue("--red").trim(),
        this.rootStyles.getPropertyValue("--grey4").trim(),
        this.rootStyles.getPropertyValue("--blue").trim(),
      ];
      return colors[index % colors.length];
    }

  /**
   * Handles pagination navigation.
   */
  nextPage() {
    if ((this.currentPage + 1) * this.pageSize < this.labels.length) {
      this.currentPage++;
      this.pageNumber++;
      this.getPaginatedData();
    }
  }

  
  /**
   * Handles pagination navigation.
   */
  prevPage() {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.pageNumber--;
      this.getPaginatedData();
    }
  }

  
  /**
   * set priority values 
   */
  setPriorityForCategories() {
    const priorityOrder = ["critical","high", "medium", "low"];
    return this.categories.sort((a:any, b:any) => {
      const aIndex = priorityOrder.indexOf(a.toLowerCase());
      const bIndex = priorityOrder.indexOf(b.toLowerCase());
      return (aIndex !== -1 ? aIndex : Infinity) - (bIndex !== -1 ? bIndex : Infinity);
    });
  }

   /** Function to shorten long labels */
   ellipsizeLabel(label: string, maxLength: number): string {
    return label.length > maxLength ? label.substring(0, maxLength) + "..." : label;
  }

  isObjectEmpty(obj: any): boolean {
    return obj && Object.keys(obj).length === 0;
  }
}
